// Mock blockchain transaction data for flow visualization
// Generates 50+ nodes and 200+ edges with realistic crypto addresses

const generateAddress = (prefix = '0x') => {
  const chars = '0123456789abcdef';
  let addr = prefix;
  for (let i = 0; i < (prefix === '0x' ? 40 : 34); i++) {
    addr += chars[Math.floor(Math.random() * chars.length)];
  }
  return addr;
};

const generateBTCAddress = () => {
  const prefixes = ['1', '3', 'bc1'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  return generateAddress(prefix);
};

const generateSOLAddress = () => {
  const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  let addr = '';
  for (let i = 0; i < 44; i++) {
    addr += chars[Math.floor(Math.random() * chars.length)];
  }
  return addr;
};

const walletLabels = [
  'Exchange Hot Wallet', 'Cold Storage', 'Trading Bot', 'DeFi Pool',
  'Mining Pool', 'Mixer Service', 'Unknown Wallet', 'CEX Deposit',
  'DEX Router', 'Staking Contract', 'Bridge Contract', 'Treasury',
  'Market Maker', 'Whale Wallet', 'Retail User', 'Smart Contract',
  'Liquidity Provider', 'Yield Farm', 'NFT Marketplace', 'DAO Treasury'
];

const riskLevels = ['low', 'medium', 'high'];

// 6 blockchains: BTC, ETH + 4 new ones
const chains = ['BTC', 'ETH', 'SOL', 'MATIC', 'AVAX', 'BNB'];

// Chain colors for visual distinction
export const chainColors = {
  BTC: '#f7931a',
  ETH: '#627eea',
  SOL: '#00ffa3',
  MATIC: '#8247e5',
  AVAX: '#e84142',
  BNB: '#f3ba2f',
};

// Chain icons (FontAwesome classes)
export const chainIcons = {
  BTC: 'fa-brands fa-bitcoin',
  ETH: 'fa-brands fa-ethereum',
  SOL: 'fa-solid fa-sun',
  MATIC: 'fa-solid fa-hexagon-vertical-nft',
  AVAX: 'fa-solid fa-mountain',
  BNB: 'fa-solid fa-coins',
};

// Generate nodes
const generateNodes = (count = 55) => {
  const nodes = [];
  
  for (let i = 0; i < count; i++) {
    const chain = chains[Math.floor(Math.random() * chains.length)];
    let address;
    
    switch (chain) {
      case 'BTC':
        address = generateBTCAddress();
        break;
      case 'SOL':
        address = generateSOLAddress();
        break;
      default: // ETH, MATIC, AVAX, BNB use 0x addresses
        address = generateAddress('0x');
    }
    
    const balance = Math.random() * 100;
    const txCount = Math.floor(Math.random() * 500) + 1;
    const risk = riskLevels[Math.floor(Math.random() * riskLevels.length)];
    
    nodes.push({
      id: `node-${i}`,
      address,
      label: walletLabels[Math.floor(Math.random() * walletLabels.length)],
      balance: parseFloat(balance.toFixed(4)),
      txCount,
      chain,
      risk,
      lastActivity: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      color: risk === 'high' ? '#ef4444' : risk === 'medium' ? '#f59e0b' : '#22c55e',
      degree: 0 // Will be calculated
    });
  }
  
  return nodes;
};

// Generate edges (transactions)
const generateEdges = (nodes, edgeCount = 220) => {
  const edges = [];
  const nodeIds = nodes.map(n => n.id);
  
  // Ensure every node has at least one connection
  nodes.forEach((node, idx) => {
    const targetIdx = (idx + 1) % nodes.length;
    const amount = Math.random() * 10;
    edges.push({
      id: `edge-init-${idx}`,
      source: node.id,
      target: nodes[targetIdx].id,
      amount: parseFloat(amount.toFixed(4)),
      timestamp: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      type: Math.random() > 0.5 ? 'inflow' : 'outflow',
      txHash: `0x${Array(64).fill(0).map(() => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('')}`
    });
  });
  
  // Add remaining random edges
  for (let i = edges.length; i < edgeCount; i++) {
    const sourceIdx = Math.floor(Math.random() * nodes.length);
    let targetIdx = Math.floor(Math.random() * nodes.length);
    
    // Avoid self-loops
    while (targetIdx === sourceIdx) {
      targetIdx = Math.floor(Math.random() * nodes.length);
    }
    
    const amount = Math.random() * 50;
    
    edges.push({
      id: `edge-${i}`,
      source: nodeIds[sourceIdx],
      target: nodeIds[targetIdx],
      amount: parseFloat(amount.toFixed(4)),
      timestamp: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      type: Math.random() > 0.5 ? 'inflow' : 'outflow',
      txHash: `0x${Array(64).fill(0).map(() => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('')}`
    });
  }
  
  return edges;
};

// Calculate node degrees
const calculateDegrees = (nodes, edges) => {
  const degreeMap = {};
  nodes.forEach(n => degreeMap[n.id] = 0);
  
  edges.forEach(e => {
    degreeMap[e.source] = (degreeMap[e.source] || 0) + 1;
    degreeMap[e.target] = (degreeMap[e.target] || 0) + 1;
  });
  
  return nodes.map(n => ({ ...n, degree: degreeMap[n.id] || 0 }));
};

// Generate complete dataset
export const generateMockData = () => {
  let nodes = generateNodes(55);
  const edges = generateEdges(nodes, 220);
  nodes = calculateDegrees(nodes, edges);
  
  return { nodes, edges };
};

// Export singleton dataset for consistent data across components
let cachedData = null;

export const getMockData = () => {
  if (!cachedData) {
    cachedData = generateMockData();
  }
  return cachedData;
};

export const refreshMockData = () => {
  cachedData = generateMockData();
  return cachedData;
};

export default getMockData;
